import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, CalendarCheck, Users } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/shannoni/circleupsquad-monthly-coaching';
const COACH_IMAGE = '/images/shannon-portrait.jpg';

const CoachPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const handleAskSquad = async () => {
    try {
      await axios.post(`${API}/support-snapshot/ask-coaching`);
      toast.success('Request sent to your circle!');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response?.data?.detail || 'Create a circle first, then you can ask your squad.');
      } else {
        toast.error('Could not send request. Try again later.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="coach-page">
      <div className="max-w-md mx-auto px-5 pt-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-col items-center text-center space-y-6">
          {/* Photo */}
          <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-cyan-200 dark:border-cyan-800 shadow-xl">
            <img src={COACH_IMAGE} alt="Shannon Ivey, Coach" className="w-full h-full object-cover object-top" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Meet Shannon
            </h1>
            <p className="text-sm text-cyan-600 dark:text-cyan-400 font-medium">
              ICF Professional Certified Coach &middot; Cancer Survivor &middot; Patient Navigator
            </p>
          </div>

          <div className="text-sm text-muted-foreground leading-relaxed space-y-3 text-left">
            <p>
              {"I'm"} Shannon, and I built CircleUpSquad because I lived it. When I was diagnosed, the hardest part wasn{"'"}t the treatment — it was coordinating the people around me without burning out.
            </p>
            <p>
              As your coach, my role is to support you in getting your support organized so this feels less frustrating and more supportive.
            </p>
            <p>
              {"I'm"} also available to help you navigate tricky patient moments, so you feel empowered to make the decisions that are best for you.
            </p>
            <p className="font-medium text-foreground">
              I am truly your advocate in this and will walk beside you.
            </p>
          </div>

          {/* CTA */}
          <div className="w-full pt-4 space-y-3">
            <Button
              onClick={() => window.open(CALENDLY_URL, '_blank')}
              className="w-full h-[52px] rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white shadow-lg shadow-cyan-600/20"
              data-testid="coach-signup-btn"
            >
              <CalendarCheck className="w-5 h-5 mr-2" /> Sign up here — first one is free
            </Button>
            <p className="text-xs text-muted-foreground/60 text-center">
              Book a free intro session. No commitment.
            </p>
          </div>

          {/* Ask your squad */}
          {user?.role === 'patient' && (
            <div className="w-full pt-2">
              <button
                onClick={handleAskSquad}
                className="w-full flex items-center justify-center gap-2.5 surface-card rounded-xl p-4 hover:border-amber-300 dark:hover:border-amber-700 transition-colors"
                data-testid="ask-squad-coaching-btn"
              >
                <Users className="w-5 h-5 text-amber-600" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">Ask your squad to gift you coaching</p>
                  <p className="text-xs text-muted-foreground">Send a hint to your circle that coaching would help</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachPage;
